/**
 * EmployeeApprovalChainSection — zeigt im Mitarbeiterprofil die
 * Urlaubs-Freigabe-Kette und erlaubt eine individuelle Konfiguration.
 *
 * - Lesemodus (alle): Default-Kette inkl. Approver-Namen
 * - Edit-Dialog (employees:edit): Toggle „Standard verwenden / Eigene Kette",
 *   pro Stufe: Label, „alle müssen freigeben"-Switch, Approver-Multiselect.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Switch } from './ui/switch';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Pencil, Plus, Trash2, Users, ShieldCheck, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import { vacationsAPI, usersAPI } from '../lib/api';

const newEmptyStep = (label = 'Eigene Stufe') => ({ label, approvers: [], require_all: true });

export const EmployeeApprovalChainSection = ({ employeeId, canEdit }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await vacationsAPI.getEmployeeApprovalChain(employeeId);
      setData(res.data);
    } catch (e) {
      // Falls Mitarbeiter ohne Profil oder ohne Rechte
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [employeeId]);
  useEffect(() => { if (employeeId) load(); }, [employeeId, load]);

  if (loading) {
    return <Card className="border-border/60"><CardContent className="p-4 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin inline-block mr-2" />Lade Freigabe-Kette …</CardContent></Card>;
  }
  if (!data) return null;

  return (
    <Card className="border-border/60" data-testid="employee-approval-chain-card">
      <CardHeader className="flex flex-row items-start justify-between space-y-0">
        <div>
          <CardTitle className="font-heading flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-primary" />
            Urlaubsfreigabe
          </CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            {data.using_custom
              ? 'Individuelle Kette aktiv — überschreibt den Standard.'
              : 'Standardregel: Standortleiter → Niederlassungsleiter.'}
          </p>
        </div>
        {canEdit && (
          <Button variant="outline" size="sm" onClick={() => setEditOpen(true)} data-testid="edit-approval-chain-btn">
            <Pencil className="h-3.5 w-3.5 mr-1" /> Bearbeiten
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {data.chain.length === 0 ? (
          <p className="text-sm text-muted-foreground italic">
            Keine Freigabe nötig (z.B. Geschäftsleitungs-Rolle).
          </p>
        ) : (
          <div className="space-y-2">
            {data.chain.map((s) => (
              <div key={s.step} className="flex items-start gap-3 p-2 rounded-md bg-muted/30">
                <Badge variant="outline" className="font-mono shrink-0">Stufe {s.step}</Badge>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">{s.label}</div>
                  <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1 flex-wrap">
                    <Users className="h-3 w-3" />
                    {(s.approver_details || []).length === 0
                      ? <span className="italic">Keine Person zugewiesen</span>
                      : (s.approver_details).map((a) => a.name).join(' · ')
                    }
                    {s.require_all && s.approver_details?.length > 1 && (
                      <Badge variant="secondary" className="ml-1 text-[10px]">alle müssen zustimmen</Badge>
                    )}
                    {!s.require_all && s.approver_details?.length > 1 && (
                      <Badge variant="secondary" className="ml-1 text-[10px]">eine Zustimmung reicht</Badge>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {editOpen && (
        <ApprovalChainEditDialog
          open={editOpen}
          onOpenChange={setEditOpen}
          employeeId={employeeId}
          initial={data}
          onSaved={(newData) => { setData(newData); setEditOpen(false); toast.success('Kette gespeichert'); }}
        />
      )}
    </Card>
  );
};

const ApprovalChainEditDialog = ({ open, onOpenChange, employeeId, initial, onSaved }) => {
  const [useCustom, setUseCustom] = useState(!!initial.using_custom);
  const [steps, setSteps] = useState(
    (initial.custom?.steps?.length ? initial.custom.steps : initial.chain).map((s) => ({
      label: s.label || 'Stufe',
      approvers: (s.approvers || s.approver_details?.map((a) => a.id) || []),
      require_all: s.require_all !== false,
    }))
  );
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    usersAPI.getAll()
      .then((res) => setUsers(res.data?.items || res.data || []))
      .catch(() => setUsers([]));
  }, []);

  const usersById = Object.fromEntries(users.map((u) => [u.id, u]));

  const updateStep = (idx, patch) => {
    setSteps((prev) => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  };
  const addApprover = (idx, userId) => {
    if (!userId) return;
    setSteps((prev) => prev.map((s, i) =>
      i === idx && !s.approvers.includes(userId)
        ? { ...s, approvers: [...s.approvers, userId] }
        : s
    ));
  };
  const removeApprover = (idx, userId) => {
    setSteps((prev) => prev.map((s, i) =>
      i === idx ? { ...s, approvers: s.approvers.filter((a) => a !== userId) } : s
    ));
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await vacationsAPI.setEmployeeApprovalChain(employeeId, {
        use_custom: useCustom,
        steps,
      });
      onSaved(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Individuelle Urlaubsfreigabe</DialogTitle>
        </DialogHeader>

        <div className="flex items-center justify-between p-3 rounded-md bg-muted/40">
          <div>
            <div className="text-sm font-medium">Eigene Freigabe-Kette verwenden</div>
            <p className="text-xs text-muted-foreground">
              Wenn deaktiviert, gilt die Standardregel (Standortleiter → Niederlassungsleiter).
            </p>
          </div>
          <Switch checked={useCustom} onCheckedChange={setUseCustom} data-testid="use-custom-toggle" />
        </div>

        {useCustom && (
          <div className="space-y-3 mt-2">
            {steps.map((s, idx) => (
              <div key={idx} className="border rounded-md p-3 space-y-2 bg-card">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex-1">
                    <Label className="text-xs">Bezeichnung Stufe {idx + 1}</Label>
                    <Input
                      value={s.label}
                      onChange={(e) => updateStep(idx, { label: e.target.value })}
                      placeholder="z.B. Standortleiter"
                    />
                  </div>
                  <Button variant="ghost" size="sm" className="mt-5 text-rose-600" onClick={() => setSteps((p) => p.filter((_, i) => i !== idx))}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>

                <div className="flex items-center justify-between p-2 rounded bg-muted/40">
                  <span className="text-xs">Alle Approver müssen freigeben (sonst reicht eine Zustimmung)</span>
                  <Switch checked={s.require_all} onCheckedChange={(v) => updateStep(idx, { require_all: v })} />
                </div>

                <div>
                  <Label className="text-xs">Approver hinzufügen</Label>
                  <Select onValueChange={(v) => addApprover(idx, v)}>
                    <SelectTrigger><SelectValue placeholder="Nutzer wählen …" /></SelectTrigger>
                    <SelectContent>
                      {users
                        .filter((u) => !s.approvers.includes(u.id))
                        .map((u) => (
                          <SelectItem key={u.id} value={u.id}>
                            {u.last_name}, {u.first_name} — {u.email}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {s.approvers.map((aid) => {
                      const u = usersById[aid];
                      return (
                        <Badge key={aid} variant="secondary" className="gap-1">
                          {u ? `${u.last_name}, ${u.first_name}` : aid.slice(0, 8)}
                          <button type="button" onClick={() => removeApprover(idx, aid)} className="hover:text-rose-600">
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      );
                    })}
                  </div>
                </div>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={() => setSteps((p) => [...p, newEmptyStep(`Stufe ${p.length + 1}`)])}>
              <Plus className="h-3 w-3 mr-1" /> Stufe hinzufügen
            </Button>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
          <Button onClick={save} disabled={saving} data-testid="save-approval-chain-btn">
            {saving && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
            Speichern
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EmployeeApprovalChainSection;
